// ConfigurationGuide.js — Configuration (refonte T7).
// Migrée sur le design system (dark mode). Chargement via require.context (conservé). Schéma inchangé
// (metadata + categories[].configurations[].parameters[]). Nettoyage (console.log + mode édition retirés).
import React, { useMemo, useState } from 'react';
import { Key, AlertTriangle, Shield, Check, HelpCircle, Settings } from 'lucide-react';
import { Card, CardContent, Badge, SearchBar, Accordion, CopyButton, EmptyState, Icon } from '../components/ui';

const configContext = require.context('../data/config', false, /\.json$/);

const ParameterDetail = ({ parameter }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
    <div className="flex justify-between items-start gap-3 mb-3">
      <div className="flex items-start gap-3 min-w-0">
        <Key className="h-5 w-5 text-blue-500 mt-1 shrink-0" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-2 py-1 rounded break-all">{parameter.key}</code>
            <CopyButton text={parameter.key} />
          </div>
          {parameter.description && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{parameter.description}</p>}
        </div>
      </div>
      {parameter.required && <Badge color="#EF4444">Requis</Badge>}
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
      <div><span className="font-medium text-gray-700 dark:text-gray-300">Type :</span> <span className="text-gray-600 dark:text-gray-400">{parameter.type}</span></div>
      {parameter.defaultValue !== null && parameter.defaultValue !== undefined && (
        <div><span className="font-medium text-gray-700 dark:text-gray-300">Défaut :</span> <code className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-1.5 py-0.5 rounded">{String(parameter.defaultValue)}</code></div>
      )}
      {parameter.validation && <div><span className="font-medium text-gray-700 dark:text-gray-300">Validation :</span> <span className="text-gray-600 dark:text-gray-400">{parameter.validation}</span></div>}
      {parameter.example && <div><span className="font-medium text-gray-700 dark:text-gray-300">Exemple :</span> <code className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 px-1.5 py-0.5 rounded">{parameter.example}</code></div>}
    </div>
    {(parameter.impact || parameter.security) && (
      <div className="mt-3 flex flex-wrap gap-4">
        {parameter.impact && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"><AlertTriangle className="h-4 w-4 text-yellow-500" />{parameter.impact}</div>}
        {parameter.security && <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"><Shield className="h-4 w-4 text-red-500" />{parameter.security}</div>}
      </div>
    )}
  </div>
);

const ConfigurationDetail = ({ config }) => (
  <Accordion
    defaultOpen
    title={<span className="flex items-center gap-2"><Settings className="h-4 w-4 text-blue-500" />{config.name}</span>}
    right={config.environment ? <Badge>{config.environment}</Badge> : null}
    className="mb-4"
  >
    {config.description && <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{config.description}</p>}
    <div className="space-y-3">
      {(config.parameters || []).map((p) => <ParameterDetail key={p.key} parameter={p} />)}
    </div>
    {config.documentation && (
      <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
        {config.documentation.setup && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Configuration</h5>
            <p className="text-sm text-gray-600 dark:text-gray-300">{config.documentation.setup}</p>
          </div>
        )}
        {config.documentation.warnings?.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-500/10 rounded-lg p-3 space-y-1">
            {config.documentation.warnings.map((w, i) => <div key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"><AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />{w}</div>)}
          </div>
        )}
        {config.documentation.bestPractices?.length > 0 && (
          <div className="bg-green-50 dark:bg-green-500/10 rounded-lg p-3 space-y-1">
            {config.documentation.bestPractices.map((b, i) => <div key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300"><Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />{b}</div>)}
          </div>
        )}
      </div>
    )}
  </Accordion>
);

const CategorySection = ({ category }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-3">
      <span className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-500"><Icon name={category.icon || 'Settings'} className="h-5 w-5" /></span>
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{category.name}</h2>
        {category.description && <p className="text-sm text-gray-600 dark:text-gray-300">{category.description}</p>}
      </div>
    </div>
    <div className="pl-2 sm:pl-14">
      {(category.configurations || []).map((c) => <ConfigurationDetail key={c.id} config={c} />)}
    </div>
  </div>
);

const ConfigurationGuide = () => {
  const configurations = useMemo(() => {
    return configContext.keys().map((key) => {
      const filename = key.replace('./', '');
      try {
        const data = configContext(key);
        if (!data.metadata || !Array.isArray(data.categories)) return null;
        const paramCount = data.categories.reduce((t, cat) => t + (cat.configurations || []).reduce((ct, cf) => ct + (cf.parameters?.length || 0), 0), 0);
        return { filename, name: data.metadata.title || filename, data, categoriesCount: data.categories.length, parametersCount: paramCount };
      } catch (e) { return null; }
    }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const [selectedFilename, setSelectedFilename] = useState(configurations[0]?.filename || null);
  const [search, setSearch] = useState('');
  const selected = configurations.find((c) => c.filename === selectedFilename) || configurations[0] || null;

  const categories = (selected?.data.categories || []).filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase()) ||
    (cat.configurations || []).some((cf) =>
      cf.name.toLowerCase().includes(search.toLowerCase()) ||
      (cf.parameters || []).some((p) => p.key.toLowerCase().includes(search.toLowerCase()) || (p.description || '').toLowerCase().includes(search.toLowerCase()))
    )
  );

  if (configurations.length === 0) return <EmptyState icon={Settings} title="Aucune configuration" message="Ajoutez des fichiers dans src/data/config/." />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Guide de configuration</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">{configurations.length} fichier(s) de configuration.</p>
      </div>

      {/* Sélecteur de fichier */}
      <div className="flex flex-wrap gap-2 mb-6">
        {configurations.map((c) => {
          const on = selected?.filename === c.filename;
          return (
            <button key={c.filename} onClick={() => setSelectedFilename(c.filename)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center gap-2 border transition-colors ${on ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
              <Settings className="h-4 w-4" />{c.name}
            </button>
          );
        })}
      </div>

      {selected && (
        <>
          <Card className="mb-6">
            <CardContent>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selected.data.metadata.title}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                v{selected.data.metadata.version || '—'}{selected.data.metadata.lastUpdate ? ` • MàJ ${selected.data.metadata.lastUpdate}` : ''} • {selected.categoriesCount} catégorie(s) • {selected.parametersCount} paramètre(s)
                {selected.data.metadata.sourceRef ? ` • src: ${selected.data.metadata.sourceRef}` : ''}
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardContent>
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><HelpCircle className="h-5 w-5 text-blue-500" />Guide d'utilisation</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Chaque paramètre indique son type, sa valeur par défaut, sa validation, son impact et sa sensibilité de sécurité. Utilisez la recherche pour filtrer par catégorie, configuration, clé ou description.</p>
            </CardContent>
          </Card>

          <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un paramètre…" className="mb-6 max-w-md" />

          {categories.length > 0 ? (
            categories.map((cat) => <CategorySection key={cat.id} category={cat} />)
          ) : (
            <EmptyState icon={Settings} title="Aucun paramètre" message={`Aucun résultat pour « ${search} ».`} />
          )}
        </>
      )}
    </div>
  );
};

export default ConfigurationGuide;
